<script setup lang="ts">
import axios from 'axios'
import type { MerchantReviewManagerConfig } from '@/types/reviewPlatforms'
import { usePlatformConfig } from '../../composables/usePlatformConfig'
import LinkedRestaurantsList from './LinkedRestaurantsList.vue'
import LinkDeliverooModal from './LinkDeliverooModal.vue'
import EditMappingModal from './EditMappingModal.vue'
import UnlinkConfirmDialog from './UnlinkConfirmDialog.vue'

const props = defineProps<{
  config?: MerchantReviewManagerConfig['deliveroo']
  isLoading?: boolean
}>()

const emit = defineEmits<{
  saved: []
}>()

const { t } = useI18n()
const { user } = useLocalStorage()
const notification = useNotification()

const configRef = toRef(props, 'config')

const {
  linkModalOpen,
  editingStoreId,
  unlinkConfirmStoreId,
  isSaving,
  linked,
  linkedExternalIds,
  availableStoresForLink,
  allStoresForEdit,
  canAddMore,
  editingRow,
  deleteStoreLink,
  confirmUnlink
} = usePlatformConfig('deliveroo', configRef, () => emit('saved'))

const hasCredentials = computed(() => props.config?.isConfigured === true)

async function saveNew(payload: {
  email?: string
  password?: string
  orgId: string
  branches: { branchId: string; storeId: number }[]
}) {
  isSaving.value = true
  try {
    await axios.post(`/merchants/${user.value.merchant.id}/review-managers/deliveroo/setup`, {
      email: payload.email ?? '',
      password: payload.password ?? '',
      org_id: payload.orgId,
      branches: payload.branches.map((b) => ({ branch_id: b.branchId, store_id: b.storeId }))
    })
    notification.create('success', { text: t('pages.reviewPlatforms.configuration.deliveroo.saveSuccess') })
    linkModalOpen.value = false
    emit('saved')
  } catch {
    notification.create('error', { text: t('pages.reviewPlatforms.configuration.deliveroo.saveError') })
  } finally {
    isSaving.value = false
  }
}

async function saveEdit(newStoreId: number) {
  if (!editingRow.value) return
  const currentRow = editingRow.value
  if (newStoreId === currentRow.storeId) {
    editingStoreId.value = null
    return
  }
  isSaving.value = true
  try {
    await deleteStoreLink(currentRow.storeId)
    await axios.post(`/merchants/${user.value.merchant.id}/review-managers/deliveroo/setup`, {
      email: '',
      password: '',
      org_id: '',
      branches: [{ branch_id: currentRow.externalStoreId, store_id: newStoreId }]
    })
    notification.create('success', { text: t('pages.reviewPlatforms.configuration.deliveroo.saveSuccess') })
    editingStoreId.value = null
    emit('saved')
  } catch {
    notification.create('error', { text: t('pages.reviewPlatforms.configuration.deliveroo.saveError') })
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div>
    <div v-if="isLoading" class="space-y-2">
      <Skeleton class="h-32 w-full rounded-xl" />
    </div>

    <LinkedRestaurantsList
      v-else
      :title="t('pages.reviewPlatforms.configuration.deliveroo.title')"
      :description="t('pages.reviewPlatforms.configuration.deliveroo.description')"
      logo-src="/images/reviews/deliveroo.svg"
      logo-alt="Deliveroo"
      :linked="linked"
      :can-add-more="canAddMore"
      :add-label="t('pages.reviewPlatforms.configuration.deliveroo.addLabel')"
      @add="linkModalOpen = true"
      @edit="(storeId) => (editingStoreId = storeId)"
      @unlink="(storeId) => (unlinkConfirmStoreId = storeId)"
    />

    <LinkDeliverooModal
      :is-open="linkModalOpen"
      :available-stores="availableStoresForLink"
      :is-saving="isSaving"
      :has-credentials="hasCredentials"
      :exclude-branch-ids="linkedExternalIds"
      @close="linkModalOpen = false"
      @save="saveNew"
    />

    <EditMappingModal
      v-if="editingRow"
      :is-open="editingStoreId !== null"
      :title="t('pages.reviewPlatforms.configuration.deliveroo.editTitle')"
      :external-id-label="t('pages.reviewPlatforms.configuration.deliveroo.branchIdLabel')"
      :external-id="editingRow.externalStoreId"
      :current-store-id="editingRow.storeId"
      :available-stores="allStoresForEdit"
      :is-saving="isSaving"
      @close="editingStoreId = null"
      @save="saveEdit"
    />

    <UnlinkConfirmDialog
      :store-id="unlinkConfirmStoreId"
      :is-saving="isSaving"
      @cancel="unlinkConfirmStoreId = null"
      @confirm="confirmUnlink"
    />
  </div>
</template>
